:root {
    --font-color: #000;
    font-size: 16px;
}

body {
    margin: 0;
    padding: 0;
    font-family: Helvetica, Arial, sans-serif, "Noto Color Emoji";
    color: var(--font-color);
}

a {
    /* accessible colors selected with http://colorsafe.co */
    color: #3455db;
    text-decoration: none;
}

a:hover {
    color: #9932cc;
}

a:active {
    color: #f1d874;
}

input[aria-invalid='true'] {
    border-color: red !important;
}

kbd {
    /* With the default monospace font, the ⇧ icon looks too small to read. */
    font-family: "system-ui", sans-serif;
}

/* improvements to PureCSS */
:global .pure-button.pure-button:focus-visible {
    outline: 1px auto #129FEA;
    outline-offset: 2px;
}

:global .pure-form button[type=submit] {
    /* PureCSS adds top margin to submit buttons on mobile, which misaligns it with the cancel button next to it */
    margin-top: 0;
}

/* layout */

.navbar {
    background-color: var(--menu-bg-color-level1);
    border-bottom: var(--menu-item-border-shadow);

    --menu-item-padding: 8px 16px;
    --menu-bg-color-level1: hsl(0, 0%, 97%);
    --menu-bg-color-level2: hsl(0, 0%, 95%);
    --menu-item-border-shadow: 1px solid hsl(0, 0%, 70%);
    --menu-item-border-highlight: 1px solid hsl(0, 0%, 100%);

    @media (width >= 768px) {
        padding: 0 0.5rem;
        display: flex;
        flex-wrap: wrap;
        align-items: stretch;

        --menu-item-padding: 2px 12px 2px 8px; /* more padding on right, to balance the emoji on left */
        --menu-item-height: 2.5rem;
    }
}

.logo {
    display: none;

    @media (width >= 768px) {
        display: initial;
        align-self: center;
        margin-right: 8px;

        :is(img) {
            width: 2rem;
            height: 2rem;
            vertical-align: bottom;
        }
    }
}

.nav-menu,
.nav-submenu {
    margin: 0;
    padding: 0;
    list-style-type: none;
    font-size: 18px;

    :is(li) {
        padding: var(--menu-item-padding);
        white-space: nowrap;
    }

    @media (width >= 768px) {
        /* each <li> should partake in the .navbar's flexbox */
        display: contents;

        :is(li) {
            align-content: center;
            height: var(--menu-item-height);
        }
    }
}

.nav-submenu > li {
    /* indentation */
    padding-left: calc(16px + 2ch);

    @media (width >= 768px) {
        padding: var(--menu-item-padding);
    }
}

.active {
    font-weight: bold;
}

.nav-menu > li:has(> .active),
.nav-submenu > li {
    background-color: var(--menu-bg-color-level2);
}

@media (width >= 768px) {
    /* borders to make the active item (or whole submenu) look 3-dimensional */
    .nav-menu > li:has(> .active),
    .nav-submenu > li {
        border-top: var(--menu-item-border-shadow);
        border-bottom: var(--menu-item-border-highlight);
    }

    .nav-menu > li:has(.active) {
        border-left: var(--menu-item-border-shadow);
    }

    .nav-submenu > li:last-of-type,
    .nav-menu > li:has(.active):not(:has(+ .nav-submenu)) {
        border-right: var(--menu-item-border-highlight);
    }
}

.nav-end {
    padding: 0 16px 4px;

    :global .pure-form label {
        /* prevent layout shift caused by Pure CSS's breakpoint at 480px width */
        display: inline;
    }

    @media (width >= 768px) {
        flex-grow: 1;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
        gap: 1rem;
        padding: 0;

        .lang, .auth {
            height: var(--menu-item-height);
        }

        .lang {
            min-width: 10rem; /* avoid layout shift when the language selector is opened */
            text-align: right;
        }
    }
}

select.languageSelection:not(:focus) {
    /* make the dropdown look like a link when it has no focus */
    color: #3455db;
    appearance: none;
    border: none;
    background: none;
    box-shadow: none;
    padding: 0;
    margin-left: 2px;
}

select.languageSelection:not(:focus):hover {
    color: #9932cc;
    cursor: pointer;
}

.languageSelectionIcon {
    font-size: 1.5em;
    vertical-align: middle;
}

.languageSelectionIcon:has(+ select.languageSelection:not(:focus)) {
    /* clicking the icon will toggle the dropdown's focus,
       so show the pointer cursor only when clicking the icon
       would focus the dropdown */
    cursor: pointer;
}

:global #htmx-error-dialog {
    color: red;

    & button {
        float: right;
        background-color: red;
    }

    &::backdrop {
        background: #fff5;
        backdrop-filter: blur(2px);
    }
}

.content {
    position: relative;
    padding: 1em 1.5em;

    &.narrow {
        max-width: 60rem;
    }

    &.full-width {
        max-width: 100%;
    }

    :is(h1) {
        margin-top: 0;
    }

    :is(h1, h2, h3, h4, h5, h6) > :is(a, a:hover) {
        color: #000000;
    }

    :is(h1, h2, h3, h4, h5, h6) > a:hover::after {
        display: inline-block;
        vertical-align: middle;
        margin-left: 1ch;
        font-size: 1rem;
        content: '🔗';
        filter: grayscale(1);
    }

    :is(p) img {
        max-width: 100%; /* fit to content, only for Markdown */
    }

    :is(li) > p:last-of-type {
        margin-bottom: 0;
    }

    :is(ol) {
        list-style-type: decimal;

        :is(ol) {
            list-style-type: lower-roman;
        }
    }

    :is(code) {
        padding: 2px 4px;
        font-size: 0.9em;
        background-color: #f3f6fa;
        border-radius: 0.3em;
    }

    :is(blockquote) {
        padding: 0 1rem;
        margin-left: 0;
        color: hsl(from var(--font-color) h s calc(l + 40));
        border-left: 0.3rem solid currentcolor;
    }
}

@media print {
    body {
        background-color: #ffffff;
    }

    .content {
        margin: 0;
        padding: 0;
    }

    :global .no-print {
        display: none;
    }
}

@page {
    size: A4 portrait;
    margin: 0;
    padding: 0;
}
